import 'react-hook-form';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import { Switch } from '@givebutter/primitives/switch';
import { useToast } from '@givebutter/primitives/toast';
import { faBank, faMoneyCheckDollar } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchAccount, updateAccount } from 'api/account';
import * as PaymentMethods from 'assets/js/pages/account-settings/account-settings/PaymentMethods';
import { StripeBillingSettings } from 'assets/js/pages/account-settings/account-settings/StripeBillingSettings';
import { Permissions } from 'components/permissions';
import { FinancePermission } from 'components/permissions/PermissionEnums';
import * as Tooltip from 'components/tooltip';
import {
    CHARIOT_HELP_LINK,
    CHARIOT_LINK,
    DEDICATIONS_HELP_LINK,
    PAYMENT_METHODS_HELP_LINK,
    RECURRING_PLANS_HELP_LINK,
} from 'constants/links';
import { Settings } from 'constants/settings';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInvalidateUserAndAccount } from 'customHooks/useInvalidateUserAndAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AccountSetting } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const GeneralSettings = () => {
    const { success, error } = useToast();
    const { settings, accountId, stripeId } = useCurrentAccount();
    const { mutate: invalidateUserData } = useInvalidateUserAndAccount();

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.current(accountId),
        queryFn: () => fetchAccount(accountId),
    });
    const account = data.data;

    const enableRecurringPaymentsDefault = settings.find(
        item => item.code === Settings.EnableRecurringPayments.Code,
    );
    const enableRecurringUpsellDefault = settings.find(
        item => item.code === Settings.EnableRecurringUpsell.Code,
    );
    const enableDedicationsDefault = settings.find(
        item => item.code === Settings.EnableDedications.Code,
    );

    const acceptedPaymentMethods = {
        check: account.payment_methods.check.enabled,
        card: account.payment_methods.card.enabled,
        ach: account.payment_methods.ach.enabled,
        digital_wallet: account.payment_methods.digital_wallet.enabled,
        venmo: account.payment_methods.venmo.enabled,
        paypal: account.payment_methods.paypal.enabled,
        donor_advised_fund: account.payment_methods.donor_advised_fund.enabled,
    };

    const submit = async (setting: AccountSetting) => {
        try {
            await updateAccount(accountId, {
                settings: [setting],
            });

            success('General settings saved successfully!');
            invalidateUserData();
        } catch (e) {
            error(
                (e as GivebutterAxiosError).response?.data.message ||
                    'Something went wrong. Please try again.',
            );
        }
    };

    const updateAcceptedPaymentMethod = (updatedSetting: { key: string; value: boolean }) => {
        submit({
            code: Settings.AccountAcceptedPaymentMethods.Code,
            value: {
                ...acceptedPaymentMethods,
                [updatedSetting.key]: updatedSetting.value,
            },
            active: true,
            name: Settings.AccountAcceptedPaymentMethods.Name,
            type: 'array',
        });
    };

    return (
        <Box className="flex flex-col">
            <div className="p-8">
                <h3 className="text-2xl font-medium">General</h3>
                <p>
                    These settings act as a template for your campaigns. You can override them on
                    each campaign.
                </p>
            </div>
            <div className="flex flex-col items-start">
                <div className="flex w-full flex-row items-start justify-between gap-4 border-b border-b-gray-200 px-8 py-4">
                    <div>
                        <Label className="text-base">Recurring Donations</Label>
                        <p className="max-w-lg text-sm">
                            Enable recurring donations across all your Givebutter campaigns.
                        </p>
                        <p>
                            <a
                                target="_blank"
                                className="text-sm"
                                rel="noopener noreferrer"
                                href={RECURRING_PLANS_HELP_LINK}
                            >
                                Learn more
                            </a>
                        </p>
                    </div>
                    <Switch
                        checked={!!enableRecurringPaymentsDefault?.active}
                        onCheckedChange={() =>
                            submit({
                                code: Settings.EnableRecurringPayments.Code,
                                value: null,
                                active: !enableRecurringPaymentsDefault?.active,
                                name: 'enable_recurring',
                                type: 'boolean',
                            })
                        }
                    />
                </div>

                {enableRecurringPaymentsDefault?.active ? (
                    <div className="flex w-full flex-row items-start justify-between gap-4 border-b border-b-gray-200 px-8 py-4">
                        <div>
                            <Label className="text-base">
                                One-time to recurring donation offer
                            </Label>
                            <p className="max-w-lg text-sm">
                                Enable an additional screen that offers the option to switch a
                                donor's one-time donation into a recurring one on the donation form.
                                This only applies if recurring donations are enabled on your
                                campaign.
                            </p>
                        </div>
                        <Switch
                            checked={!!enableRecurringUpsellDefault?.active}
                            onCheckedChange={() =>
                                submit({
                                    code: Settings.EnableRecurringUpsell.Code,
                                    value: null,
                                    active: !enableRecurringUpsellDefault?.active,
                                    name: 'recurring_upsell',
                                    type: 'boolean',
                                })
                            }
                        />
                    </div>
                ) : null}

                <div className="flex w-full flex-row items-start justify-between gap-4 border-b border-b-gray-200 px-8 py-4">
                    <div>
                        <Label className="text-base">Dedications (In Honor / In Memory of)</Label>
                        <p className="max-w-lg text-sm">
                            Enable a way for donors to give in honor of or in memory of someone.
                        </p>
                        <p>
                            <a
                                target="_blank"
                                className="text-sm"
                                rel="noopener noreferrer"
                                href={DEDICATIONS_HELP_LINK}
                            >
                                Learn more
                            </a>
                        </p>
                    </div>
                    <Switch
                        checked={!!enableDedicationsDefault?.active}
                        onCheckedChange={() =>
                            submit({
                                code: Settings.EnableDedications.Code,
                                value: null,
                                active: !enableDedicationsDefault?.active,
                                name: 'dedications',
                                type: 'boolean',
                            })
                        }
                    />
                </div>

                <Permissions allowed={[FinancePermission.SETTINGS]}>
                    <div className="flex w-full flex-col gap-4 px-8 py-4">
                        <div>
                            <Label className="text-base">Payment methods</Label>
                            <p className="max-w-lg text-sm">
                                Manage the accepted payment methods across your account.{' '}
                                <a
                                    href={PAYMENT_METHODS_HELP_LINK}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Learn more
                                </a>
                            </p>
                        </div>

                        <PaymentMethods.Root>
                            <PaymentMethods.Option>
                                <PaymentMethods.Label>
                                    <PaymentMethods.Trigger>
                                        Credit & debit cards
                                        <div className="flex flex-row items-center gap-1">
                                            <img
                                                alt="Visa"
                                                src={`/images/logos/payment_methods/visa.svg`}
                                            />
                                            <img
                                                alt="Mastercard"
                                                src={`/images/logos/payment_methods/mastercard.svg`}
                                            />
                                            <img
                                                alt="Discover"
                                                src={`/images/logos/payment_methods/discover.svg`}
                                            />
                                            <img
                                                alt="American Express"
                                                src={`/images/logos/payment_methods/american_express.svg`}
                                            />
                                            <img
                                                alt="JCB"
                                                src={`/images/logos/payment_methods/jcb.svg`}
                                            />
                                            <img
                                                alt="Diners Club"
                                                src={`/images/logos/payment_methods/diners_club.svg`}
                                            />
                                        </div>
                                    </PaymentMethods.Trigger>
                                    <Badge size="sm" color="success">
                                        Enabled
                                    </Badge>
                                </PaymentMethods.Label>
                                <PaymentMethods.Content>
                                    <PaymentMethods.DescriptionContainer>
                                        <PaymentMethods.Description>
                                            Accept all major credit and debit cards, including Visa,
                                            MasterCard, American Express, JCB, Discover, and Diners
                                            Club.
                                        </PaymentMethods.Description>
                                        <PaymentMethods.DescriptionSeparator />
                                        <PaymentMethods.DetailsContainer>
                                            <PaymentMethods.ProcessingFee>
                                                2.9% + 30¢ per transaction.
                                            </PaymentMethods.ProcessingFee>
                                            <PaymentMethods.Compatibility>
                                                Available for all transaction types.
                                            </PaymentMethods.Compatibility>
                                        </PaymentMethods.DetailsContainer>
                                    </PaymentMethods.DescriptionContainer>

                                    <AlertNotification.Root type="info">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Description>
                                                This payment method cannot be disabled.
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                </PaymentMethods.Content>
                            </PaymentMethods.Option>

                            <PaymentMethods.Option>
                                <PaymentMethods.Label>
                                    <PaymentMethods.Trigger>
                                        Digital wallet
                                        <div className="flex flex-row items-center gap-2">
                                            <img
                                                alt="Apple Pay"
                                                src={`/images/logos/payment_methods/apple_pay.svg`}
                                            />
                                            <img
                                                alt="Google Pay"
                                                src={`/images/logos/payment_methods/google_pay.svg`}
                                            />
                                        </div>
                                    </PaymentMethods.Trigger>
                                    <Badge size="sm" color="success">
                                        Enabled
                                    </Badge>
                                </PaymentMethods.Label>
                                <PaymentMethods.Content>
                                    <PaymentMethods.DescriptionContainer>
                                        <PaymentMethods.Description>
                                            Digital wallets will automatically be displayed based on
                                            device and browser compatibility.
                                            <br />
                                            <a
                                                className="whitespace-nowrap"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={PAYMENT_METHODS_HELP_LINK + '#h_a0a6893ae9'}
                                            >
                                                Learn More &rarr;
                                            </a>
                                        </PaymentMethods.Description>
                                        <PaymentMethods.DescriptionSeparator />
                                        <PaymentMethods.DetailsContainer>
                                            <PaymentMethods.ProcessingFee>
                                                2.9% + 30¢ per transaction.
                                            </PaymentMethods.ProcessingFee>
                                            <PaymentMethods.Compatibility>
                                                Not available for recurring donations or for auction
                                                auto-charge. ApplePay is not available on embedded
                                                widgets.
                                            </PaymentMethods.Compatibility>
                                        </PaymentMethods.DetailsContainer>
                                    </PaymentMethods.DescriptionContainer>
                                    <AlertNotification.Root type="info">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Description>
                                                This payment method cannot be disabled.
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                </PaymentMethods.Content>
                            </PaymentMethods.Option>

                            <PaymentMethods.Option>
                                <PaymentMethods.Label>
                                    <PaymentMethods.Trigger>
                                        PayPal
                                        <div>
                                            <img
                                                alt="PayPal logo"
                                                src={`/images/logos/payment_methods/paypal.svg`}
                                            />
                                        </div>
                                    </PaymentMethods.Trigger>

                                    <Badge
                                        size="sm"
                                        color={
                                            acceptedPaymentMethods['paypal'] ? 'success' : 'error'
                                        }
                                    >
                                        {acceptedPaymentMethods['paypal'] ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </PaymentMethods.Label>
                                <PaymentMethods.Content>
                                    <PaymentMethods.DescriptionContainer>
                                        <PaymentMethods.Description>
                                            Allow supporters to pay using their PayPal account.
                                        </PaymentMethods.Description>
                                        <PaymentMethods.DescriptionSeparator />
                                        <PaymentMethods.DetailsContainer>
                                            <PaymentMethods.ProcessingFee>
                                                2.9% + 30¢ per transaction.
                                            </PaymentMethods.ProcessingFee>
                                            <PaymentMethods.Compatibility>
                                                Available for all transaction types.
                                            </PaymentMethods.Compatibility>
                                        </PaymentMethods.DetailsContainer>
                                    </PaymentMethods.DescriptionContainer>

                                    <PaymentMethods.Actions>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="neutral"
                                            onClick={() =>
                                                updateAcceptedPaymentMethod({
                                                    key: 'paypal',
                                                    value: !acceptedPaymentMethods['paypal'],
                                                })
                                            }
                                        >
                                            {acceptedPaymentMethods['paypal']
                                                ? 'Disable'
                                                : 'Enable'}
                                        </Button>
                                    </PaymentMethods.Actions>
                                </PaymentMethods.Content>
                            </PaymentMethods.Option>

                            <PaymentMethods.Option>
                                <PaymentMethods.Label>
                                    <PaymentMethods.Trigger>
                                        Venmo
                                        <div>
                                            <img
                                                alt="Venmo logo"
                                                src={`/images/logos/payment_methods/venmo.svg`}
                                            />
                                        </div>
                                    </PaymentMethods.Trigger>

                                    <Badge
                                        size="sm"
                                        color={
                                            acceptedPaymentMethods['venmo'] ? 'success' : 'error'
                                        }
                                    >
                                        {acceptedPaymentMethods['venmo'] ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </PaymentMethods.Label>
                                <PaymentMethods.Content>
                                    <PaymentMethods.DescriptionContainer>
                                        <PaymentMethods.Description>
                                            Supporters must have the Venmo app installed and access
                                            your campaign from the native browser of their device
                                            (Safari on iOS or Chrome on Android.)
                                            <br />
                                            <a
                                                className="whitespace-nowrap"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={PAYMENT_METHODS_HELP_LINK + '#h_e47b41517b'}
                                            >
                                                Learn More &rarr;
                                            </a>
                                        </PaymentMethods.Description>
                                        <PaymentMethods.DescriptionSeparator />
                                        <PaymentMethods.DetailsContainer>
                                            <PaymentMethods.ProcessingFee>
                                                2.9% + 30¢ per transaction.
                                            </PaymentMethods.ProcessingFee>
                                            <PaymentMethods.Compatibility>
                                                Available for all transaction types, with the
                                                exception of recurring donations and auction
                                                auto-charge.
                                            </PaymentMethods.Compatibility>
                                        </PaymentMethods.DetailsContainer>
                                    </PaymentMethods.DescriptionContainer>

                                    <PaymentMethods.Actions>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            color="neutral"
                                            onClick={() =>
                                                updateAcceptedPaymentMethod({
                                                    key: 'venmo',
                                                    value: !acceptedPaymentMethods['venmo'],
                                                })
                                            }
                                        >
                                            {acceptedPaymentMethods['venmo'] ? 'Disable' : 'Enable'}
                                        </Button>
                                    </PaymentMethods.Actions>
                                </PaymentMethods.Content>
                            </PaymentMethods.Option>

                            {account.payment_methods.ach.eligible && (
                                <PaymentMethods.Option>
                                    <PaymentMethods.Label>
                                        <PaymentMethods.Trigger>
                                            US bank transfer (ACH)
                                            <div className="flex items-center gap-6">
                                                <FontAwesomeIcon
                                                    icon={faBank}
                                                    className="text-gray-400"
                                                />
                                            </div>
                                        </PaymentMethods.Trigger>
                                        <Badge
                                            size="sm"
                                            color={
                                                acceptedPaymentMethods['ach'] ? 'success' : 'error'
                                            }
                                        >
                                            {acceptedPaymentMethods['ach'] ? 'Enabled' : 'Disabled'}
                                        </Badge>
                                    </PaymentMethods.Label>
                                    <PaymentMethods.Content>
                                        <PaymentMethods.DescriptionContainer>
                                            <PaymentMethods.Description>
                                                Verified nonprofits can allow supporters to donate
                                                using their US bank account.
                                                <br />
                                                <a
                                                    className="whitespace-nowrap"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={
                                                        PAYMENT_METHODS_HELP_LINK + '#h_2154f5a5d2'
                                                    }
                                                >
                                                    Learn More &rarr;
                                                </a>
                                            </PaymentMethods.Description>
                                            <PaymentMethods.DescriptionSeparator />
                                            <PaymentMethods.DetailsContainer>
                                                <PaymentMethods.ProcessingFee>
                                                    1.9% + 30¢ per transaction. This is 1% lower
                                                    than all other payment methods.
                                                </PaymentMethods.ProcessingFee>
                                                <PaymentMethods.Compatibility>
                                                    Available for all transaction types, with the
                                                    exception auction auto-charge.
                                                </PaymentMethods.Compatibility>
                                            </PaymentMethods.DetailsContainer>
                                        </PaymentMethods.DescriptionContainer>

                                        <PaymentMethods.Actions>
                                            {account.payment_methods.ach.can_be_enabled && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() =>
                                                        updateAcceptedPaymentMethod({
                                                            key: 'ach',
                                                            value: !acceptedPaymentMethods['ach'],
                                                        })
                                                    }
                                                >
                                                    {acceptedPaymentMethods['ach']
                                                        ? 'Disable'
                                                        : 'Enable'}
                                                </Button>
                                            )}

                                            {!account.payment_methods.ach.can_be_enabled && (
                                                <Tooltip.Root>
                                                    <Tooltip.Trigger asChild>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            color="neutral"
                                                            disabled
                                                        >
                                                            Enable
                                                        </Button>
                                                    </Tooltip.Trigger>
                                                    <Tooltip.Portal>
                                                        <Tooltip.Content inverse>
                                                            <p>
                                                                Your account must be verified to
                                                                enable ACH payments.
                                                            </p>
                                                            <Tooltip.Arrow inverse />
                                                        </Tooltip.Content>
                                                    </Tooltip.Portal>
                                                </Tooltip.Root>
                                            )}
                                        </PaymentMethods.Actions>
                                    </PaymentMethods.Content>
                                </PaymentMethods.Option>
                            )}

                            <PaymentMethods.Option>
                                <PaymentMethods.Label>
                                    <PaymentMethods.Trigger>
                                        Check
                                        <div>
                                            <FontAwesomeIcon
                                                icon={faMoneyCheckDollar}
                                                className="text-gray-400"
                                            />
                                        </div>
                                    </PaymentMethods.Trigger>
                                </PaymentMethods.Label>
                                <PaymentMethods.Content>
                                    <PaymentMethods.DescriptionContainer>
                                        <PaymentMethods.Description>
                                            Accept donations via check on your forms. This method
                                            requires a mailing address and any necessary
                                            instructions to be added.
                                            <br />
                                            <a
                                                className="whitespace-nowrap"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                href={PAYMENT_METHODS_HELP_LINK + '#h_ebb7842836'}
                                            >
                                                Learn More &rarr;
                                            </a>
                                        </PaymentMethods.Description>
                                        <PaymentMethods.DescriptionSeparator />
                                        <PaymentMethods.DetailsContainer>
                                            <PaymentMethods.ProcessingFee>
                                                <span className="italic">
                                                    No processing fee for offline payments.
                                                </span>
                                            </PaymentMethods.ProcessingFee>
                                            <PaymentMethods.Compatibility>
                                                Not available for recurring donations or when
                                                purchasing an auction item.
                                            </PaymentMethods.Compatibility>
                                        </PaymentMethods.DetailsContainer>
                                    </PaymentMethods.DescriptionContainer>
                                    <AlertNotification.Root type="info">
                                        <AlertNotification.Icon />
                                        <AlertNotification.Content>
                                            <AlertNotification.Description>
                                                Visit your campaign settings to enable check
                                                donations. You can also add a check payment manually
                                                in the Transactions tab.
                                            </AlertNotification.Description>
                                        </AlertNotification.Content>
                                    </AlertNotification.Root>
                                </PaymentMethods.Content>
                            </PaymentMethods.Option>

                            {account.payment_methods.donor_advised_fund.eligible && (
                                <PaymentMethods.Option>
                                    <PaymentMethods.Label>
                                        <PaymentMethods.Trigger>
                                            Donor Advised Fund
                                            <div>
                                                <img
                                                    alt="DAFpay logo"
                                                    className="max-w-14"
                                                    src={`/images/logos/payment_methods/dafpay.svg`}
                                                />
                                            </div>
                                        </PaymentMethods.Trigger>

                                        <Badge
                                            size="sm"
                                            color={
                                                acceptedPaymentMethods['donor_advised_fund']
                                                    ? 'success'
                                                    : 'error'
                                            }
                                        >
                                            {acceptedPaymentMethods['donor_advised_fund']
                                                ? 'Enabled'
                                                : 'Disabled'}
                                        </Badge>
                                    </PaymentMethods.Label>
                                    <PaymentMethods.Content>
                                        <PaymentMethods.DescriptionContainer>
                                            <PaymentMethods.Description>
                                                Powered by{' '}
                                                <a
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={CHARIOT_LINK}
                                                    className="font-normal text-gray-900 underline"
                                                >
                                                    Chariot
                                                </a>{' '}
                                                and available for verified nonprofit accounts.
                                                <br />
                                                <a
                                                    className="whitespace-nowrap"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    href={CHARIOT_HELP_LINK}
                                                >
                                                    Learn More &rarr;
                                                </a>
                                            </PaymentMethods.Description>
                                            <PaymentMethods.DescriptionSeparator />
                                            <PaymentMethods.DetailsContainer>
                                                <PaymentMethods.ProcessingFee>
                                                    2.9% + 30¢ per transaction. No platform fees or
                                                    tips apply.
                                                </PaymentMethods.ProcessingFee>
                                                <PaymentMethods.Compatibility>
                                                    Only available for donations.
                                                </PaymentMethods.Compatibility>
                                                <div className="text-sm">
                                                    <Label>Payouts</Label>
                                                    <div className="flex flex-col gap-2">
                                                        <p>
                                                            If your organization has a Chariot
                                                            account, DAFpay donations will be
                                                            distributed directly from the DAF
                                                            provider. You can track the grant status
                                                            of DAFpay grants through your Chariot
                                                            dashboard. Grants will generally be
                                                            distributed via check, unless ACH
                                                            distributions are setup directly with
                                                            the DAF provider.
                                                        </p>
                                                        <p>
                                                            If your organization does not have a
                                                            Chariot account, donations will be
                                                            distributed via check through the DAFpay
                                                            Network. Checks will be sent to your
                                                            organizations IRS mailing address.
                                                        </p>
                                                        <p>
                                                            We recommend signing up for a Chariot
                                                            account for the most seamless DAFpay
                                                            experience.
                                                        </p>
                                                    </div>
                                                </div>
                                            </PaymentMethods.DetailsContainer>
                                        </PaymentMethods.DescriptionContainer>
                                        <PaymentMethods.Actions>
                                            {account.payment_methods.donor_advised_fund
                                                .eligible && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    color="neutral"
                                                    onClick={() =>
                                                        updateAcceptedPaymentMethod({
                                                            key: 'donor_advised_fund',
                                                            value: !acceptedPaymentMethods[
                                                                'donor_advised_fund'
                                                            ],
                                                        })
                                                    }
                                                >
                                                    {acceptedPaymentMethods['donor_advised_fund']
                                                        ? 'Disable'
                                                        : 'Enable'}
                                                </Button>
                                            )}

                                            {!account.payment_methods.donor_advised_fund
                                                .eligible && (
                                                <Tooltip.Root>
                                                    <Tooltip.Trigger asChild>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            color="neutral"
                                                            disabled
                                                        >
                                                            Enable
                                                        </Button>
                                                    </Tooltip.Trigger>
                                                    <Tooltip.Portal>
                                                        <Tooltip.Content inverse>
                                                            <p>
                                                                Your account must be verified to
                                                                enable Donor Advised Fund payments.
                                                            </p>
                                                            <Tooltip.Arrow inverse />
                                                        </Tooltip.Content>
                                                    </Tooltip.Portal>
                                                </Tooltip.Root>
                                            )}
                                        </PaymentMethods.Actions>
                                    </PaymentMethods.Content>
                                </PaymentMethods.Option>
                            )}
                        </PaymentMethods.Root>
                    </div>
                </Permissions>

                {stripeId && (
                    <>
                        <Separator className="bg-gray-200" />

                        <div className="flex w-full flex-col gap-4 px-8 py-4">
                            <StripeBillingSettings />
                        </div>
                    </>
                )}
            </div>
        </Box>
    );
};

export { GeneralSettings };
